import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const OUTCOMES = ['positive','neutral','negative'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const outcomeColors = { positive: 'success', neutral: 'neutral', negative: 'error' };
const outcomeDotColors = { positive: '#10b981', neutral: '#f59e0b', negative: '#ef4444' };

const defaultForm = {
  customer_id: '', visit_date: '', visit_time: '', purpose: '', products_discussed: '',
  notes: '', outcome: 'neutral', samples_given: '', next_visit_date: '',
  dcr_purpose: '', dcr_products_discussed: '', dcr_samples_given: '',
  dcr_next_action: '', dcr_doctor_feedback: '', dcr_submitted: false,
};

export default function Visits() {
  const { user, isPrivileged } = useAuth();
  const now = new Date();

  const [tab, setTab] = useState('visits'); // 'visits' | 'dcr'
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [viewModal, setViewModal] = useState({ open: false, visit: null });
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [dcrOpen, setDcrOpen] = useState(false);

  // Calendar view state
  const [calView, setCalView] = useState(false);
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth()); // 0-indexed
  const [calDayPopup, setCalDayPopup] = useState(null); // { date, visits }

  // DCR Report state
  const [dcrRows, setDcrRows] = useState([]);
  const [dcrLoading, setDcrLoading] = useState(false);
  const [dcrYear, setDcrYear] = useState(now.getFullYear());
  const [dcrMonth, setDcrMonth] = useState(now.getMonth() + 1);
  const [dcrUserId, setDcrUserId] = useState('');
  const [dcrUsers, setDcrUsers] = useState([]);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/visits', { params: { page, limit: 15, search: debouncedSearch||undefined, outcome: outcomeFilter||undefined } });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, outcomeFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDCR = useCallback(async () => {
    setDcrLoading(true);
    try {
      const params = { year: dcrYear, month: dcrMonth };
      if (dcrUserId) params.user_id = dcrUserId;
      const { data: res } = await api.get('/visits/dcr-report', { params });
      setDcrRows(res.data?.data || res.data || []);
    } catch { setDcrRows([]); }
    finally { setDcrLoading(false); }
  }, [dcrYear, dcrMonth, dcrUserId]);

  useEffect(() => {
    if (tab === 'dcr') {
      loadDCR();
      if (isPrivileged) {
        api.get('/users', { params: { limit: 200, role: 'mr' } }).then(r => {
          const users = r.data?.data || r.data || [];
          setDcrUsers(Array.isArray(users) ? users : []);
        }).catch(() => setDcrUsers([]));
      }
    }
  }, [tab, loadDCR, isPrivileged]);

  const openAdd = async () => {
    const { data: c } = await api.get('/customers', { params: { limit: 200 } });
    setCustomers(c.data || []);
    setForm({ ...defaultForm, visit_date: new Date().toISOString().split('T')[0] });
    setError(''); setDcrOpen(false); setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = async (v) => {
    const { data: c } = await api.get('/customers', { params: { limit: 200 } });
    setCustomers(c.data || []);
    setForm({
      customer_id: v.customer_id, visit_date: v.visit_date?.split('T')[0] || '', visit_time: v.visit_time || '',
      purpose: v.purpose || '', products_discussed: v.products_discussed || '',
      notes: v.notes || '', outcome: v.outcome || 'neutral',
      samples_given: v.samples_given || '', next_visit_date: v.next_visit_date?.split('T')[0] || '',
      dcr_purpose: v.dcr_purpose || '', dcr_products_discussed: v.dcr_products_discussed || '',
      dcr_samples_given: v.dcr_samples_given || '', dcr_next_action: v.dcr_next_action || '',
      dcr_doctor_feedback: v.dcr_doctor_feedback || '', dcr_submitted: !!v.dcr_submitted,
    });
    setError(''); setDcrOpen(false); setModal({ open: true, mode: 'edit', item: v });
  };

  const handleSave = async () => {
    if (!form.customer_id || !form.visit_date) { setError('Customer and visit date are required.'); return; }
    setSaving(true); setError('');
    try {
      if (modal.mode === 'add') await api.post('/visits', form);
      else await api.put(`/visits/${modal.item.id}`, form);
      setModal({ open: false }); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await api.delete(`/visits/${deleteConfirm.id}`);
    setDeleteConfirm(null); load();
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/visits/${id}/unfreeze`); load(); setModal({ open: false }); }
    catch (err) { setError(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const exportDCR = () => {
    if (!dcrRows.length) return;
    const headers = ['Date','MR Name','Customer','Type','Purpose','Products Discussed','Samples Given','Next Action','Feedback'];
    const csvRows = dcrRows.map(r => [
      r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN') : '',
      r.mr_name || '',
      r.customer_name || '',
      r.customer_type || '',
      r.dcr_purpose || '',
      r.dcr_products_discussed || '',
      r.dcr_samples_given || '',
      r.dcr_next_action || '',
      r.dcr_doctor_feedback || '',
    ].map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DCR_Report_${MONTHS[dcrMonth-1]}_${dcrYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Visits</h1>
          <p className="page-subtitle">Track doctor and customer visits by Medical Representatives</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${tab === 'dcr' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(tab === 'dcr' ? 'visits' : 'dcr')}
          >
            DCR Report
          </button>
          {tab === 'visits' && (
            <button
              className={`btn ${calView ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCalView(v => !v)}
              title={calView ? 'Switch to Table View' : 'Switch to Calendar View'}
            >
              {calView ? (
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              ) : (
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              )}
              {calView ? 'Table' : 'Calendar'}
            </button>
          )}
          {tab === 'visits' && (
            <button className="btn btn-primary" onClick={() => { setCalDayPopup(null); openAdd(); }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Record Visit
            </button>
          )}
        </div>
      </div>

      {/* Tab: Visits List */}
      {tab === 'visits' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '14px 16px' }}>
              <div className="search-bar">
                <div className="search-input-wrap">
                  <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                  <input className="form-control" placeholder="Search customer, purpose…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <select className="form-control form-select" style={{ width: 160 }} value={outcomeFilter} onChange={e => { setOutcomeFilter(e.target.value); setPage(1); }}>
                  <option value="">All Outcomes</option>
                  {OUTCOMES.map(o => <option key={o} value={o} style={{ textTransform: 'capitalize' }}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── Calendar View ──────────────────────────────────── */}
          {calView && (() => {
            // Build a map of visit_date → visits[]
            const allVisits = data.data;
            const visitsByDate = {};
            allVisits.forEach(v => {
              const d = v.visit_date ? v.visit_date.split('T')[0] : null;
              if (d) {
                if (!visitsByDate[d]) visitsByDate[d] = [];
                visitsByDate[d].push(v);
              }
            });

            const firstDay = new Date(calYear, calMonth, 1).getDay(); // 0=Sun
            const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
            const todayStr = new Date().toISOString().split('T')[0];

            const prevMonth = () => {
              if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
              else setCalMonth(m => m - 1);
            };
            const nextMonth = () => {
              if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
              else setCalMonth(m => m + 1);
            };

            const cells = [];
            // Empty cells before first day
            for (let i = 0; i < firstDay; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);

            return (
              <div className="card" style={{ padding: '16px 20px', marginBottom: 16, position: 'relative' }}>
                {/* Calendar header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <button className="btn btn-secondary btn-sm" onClick={prevMonth}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{MONTHS_FULL[calMonth]} {calYear}</span>
                  <button className="btn btn-secondary btn-sm" onClick={nextMonth}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {[['positive','#10b981','Positive'],['neutral','#f59e0b','Neutral'],['negative','#ef4444','Negative']].map(([k,c,l]) => (
                    <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}
                    </span>
                  ))}
                </div>

                {/* Weekday headers */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
                  {WEEKDAYS.map(d => (
                    <div key={d} style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
                  {cells.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayVisits = visitsByDate[dateStr] || [];
                    const isToday = dateStr === todayStr;
                    const isSelected = calDayPopup?.date === dateStr;
                    return (
                      <div key={day}
                        onClick={() => {
                          if (isSelected) { setCalDayPopup(null); return; }
                          setCalDayPopup({ date: dateStr, visits: dayVisits });
                        }}
                        style={{
                          minHeight: 58, padding: '5px 6px', borderRadius: 6, cursor: 'pointer',
                          border: `1.5px solid ${isSelected ? 'var(--primary)' : isToday ? '#bfdbfe' : 'var(--border)'}`,
                          background: isSelected ? 'var(--primary-lighter)' : isToday ? '#f0f9ff' : 'var(--surface)',
                          transition: 'border-color 0.12s, background 0.12s',
                          position: 'relative',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--primary-light, #93c5fd)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = isToday ? '#bfdbfe' : 'var(--border)'; }}
                      >
                        <div style={{ fontSize: '0.78rem', fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--primary)' : 'var(--text-secondary)', marginBottom: 4 }}>{day}</div>
                        {dayVisits.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {dayVisits.slice(0, 6).map((v, i) => (
                              <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: outcomeDotColors[v.outcome] || '#9ca3af', display: 'inline-block' }} />
                            ))}
                            {dayVisits.length > 6 && <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>+{dayVisits.length - 6}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Day popup */}
                {calDayPopup && (
                  <div style={{
                    position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
                    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.16)', zIndex: 30, minWidth: 280, maxWidth: 400,
                    padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        {new Date(calDayPopup.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={async () => {
                          const dateToFill = calDayPopup.date;
                          setCalDayPopup(null);
                          await openAdd();
                          setForm(p => ({ ...p, visit_date: dateToFill }));
                        }} style={{ fontSize: '0.72rem', padding: '3px 10px' }}>
                          + Add Visit
                        </button>
                        <button onClick={() => setCalDayPopup(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                    </div>
                    {calDayPopup.visits.length === 0 ? (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', padding: '6px 0' }}>No visits on this day.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                        {calDayPopup.visits.map(v => (
                          <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', background: 'var(--surface-2)', borderRadius: 7 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: outcomeDotColors[v.outcome], flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{v.customer_name}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{v.purpose || '—'}{v.user_name ? ` · ${v.user_name}` : ''}</div>
                            </div>
                            <button className="btn btn-ghost btn-icon" style={{ padding: 3 }} onClick={() => { setCalDayPopup(null); setViewModal({ open: true, visit: v }); }}>
                              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Table View ──────────────────────────────────────── */}
          {!calView && <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th><th>Visit Date</th><th>Purpose</th><th>Outcome</th>
                    <th>DCR</th><th>Next Visit</th><th>MR</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner"/></div></td></tr>}
                  {!loading && data.data.length === 0 && <tr><td colSpan={8}><div className="empty-state"><p>No visits recorded yet.</p></div></td></tr>}
                  {!loading && data.data.map(v => (
                    <tr key={v.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: v.is_active === 0 ? 'var(--text-muted)' : undefined }}>
                          {v.customer_name}
                          {v.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize: '0.62rem', marginLeft: 6 }}>Frozen</span>}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{v.customer_type}{v.specialty ? ` · ${v.specialty}` : ''}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</div>
                        {v.visit_time && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{v.visit_time}</div>}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', maxWidth: 180 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.purpose || '—'}</div>
                      </td>
                      <td><span className={`badge badge-${outcomeColors[v.outcome]}`} style={{ textTransform: 'capitalize' }}>{v.outcome}</span></td>
                      <td>
                        {v.dcr_submitted
                          ? <span style={{ color: '#16a34a', fontSize: '1rem' }}>&#10003;</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                        {v.next_visit_date ? new Date(v.next_visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{v.user_name}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => setViewModal({ open: true, visit: v })}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          {(isPrivileged || v.user_id === user.id) && (
                            <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(v)}>
                              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                          )}
                          {(isPrivileged || v.user_id === user.id) && v.is_active !== 0 && (
                            <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(v)}>
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
          </div>}
        </>
      )}

      {/* Tab: DCR Report */}
      {tab === 'dcr' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>Month:</label>
                  <select className="form-control form-select" style={{ width: 110 }} value={dcrMonth} onChange={e => setDcrMonth(parseInt(e.target.value))}>
                    {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>Year:</label>
                  <select className="form-control form-select" style={{ width: 95 }} value={dcrYear} onChange={e => setDcrYear(parseInt(e.target.value))}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {isPrivileged && dcrUsers.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>MR:</label>
                    <select className="form-control form-select" style={{ width: 160 }} value={dcrUserId} onChange={e => setDcrUserId(e.target.value)}>
                      <option value="">All MRs</option>
                      {dcrUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                )}
                <button className="btn btn-secondary" onClick={loadDCR}>Refresh</button>
                <button className="btn btn-secondary" onClick={exportDCR} disabled={!dcrRows.length}>
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>MR Name</th><th>Customer</th><th>Purpose</th>
                    <th>Products Discussed</th><th>Samples Given</th><th>Next Action</th><th>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {dcrLoading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
                  {!dcrLoading && dcrRows.length === 0 && (
                    <tr><td colSpan={8}><div className="empty-state"><p>No submitted DCRs for this period.</p></div></td></tr>
                  )}
                  {!dcrLoading && dcrRows.map(r => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.83rem' }}>
                        {r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                      </td>
                      <td style={{ fontWeight: 500 }}>{r.mr_name || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.customer_name || '—'}</div>
                        {r.customer_type && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{r.customer_type}</div>}
                      </td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.dcr_purpose || '—'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_products_discussed || '—'}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_samples_given || '—'}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_next_action || '—'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_doctor_feedback || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal open={modal.open} onClose={() => setModal({ open: false })} title={modal.mode === 'add' ? 'Record Visit' : 'Edit Visit'}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
          {modal.item?.is_active === 0
            ? <button className="btn btn-primary" style={{ background: '#dc2626' }} onClick={() => handleUnfreeze(modal.item.id)}>Unfreeze</button>
            : <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Visit'}</button>
          }
        </>}
      >
        {error && <div className="alert alert-error">{error}</div>}
        {modal.item?.is_active === 0 && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>Record Frozen</div>
            <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This record is frozen and cannot be edited. Unfreeze to make changes.</div>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Customer *</label>
            <select className="form-control form-select" value={form.customer_id} onChange={e => f('customer_id', e.target.value)} disabled={modal.item?.is_active === 0}>
              <option value="">Select customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <select className="form-control form-select" value={form.outcome} onChange={e => f('outcome', e.target.value)} disabled={modal.item?.is_active === 0}>
              {OUTCOMES.map(o => <option key={o} value={o} style={{ textTransform: 'capitalize' }}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Visit Date *</label><input className="form-control" type="date" value={form.visit_date} onChange={e => f('visit_date', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Visit Time</label><input className="form-control" type="time" value={form.visit_time} onChange={e => f('visit_time', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-group"><label className="form-label">Purpose</label><input className="form-control" value={form.purpose} onChange={e => f('purpose', e.target.value)} placeholder="Product promotion, Follow-up, Sample distribution…" disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Products Discussed</label><input className="form-control" value={form.products_discussed} onChange={e => f('products_discussed', e.target.value)} placeholder="Paracetamol 500mg, Amoxicillin 250mg…" disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Samples Given</label><input className="form-control" value={form.samples_given} onChange={e => f('samples_given', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Next Visit Date</label><input className="form-control" type="date" value={form.next_visit_date} onChange={e => f('next_visit_date', e.target.value)} disabled={modal.item?.is_active === 0} /></div>

        {/* DCR Section */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => setDcrOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--primary)', padding: 0, marginBottom: dcrOpen ? 12 : 0 }}
            disabled={modal.item?.is_active === 0}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ transform: dcrOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            DCR Details
          </button>
          {dcrOpen && (
            <div>
              <div className="form-group">
                <label className="form-label">Purpose of Visit (DCR)</label>
                <input className="form-control" value={form.dcr_purpose} onChange={e => f('dcr_purpose', e.target.value)} placeholder="Detailed purpose for DCR…" disabled={modal.item?.is_active === 0} />
              </div>
              <div className="form-group">
                <label className="form-label">Products Discussed (DCR)</label>
                <textarea className="form-control" rows={2} value={form.dcr_products_discussed} onChange={e => f('dcr_products_discussed', e.target.value)} placeholder="List products discussed…" disabled={modal.item?.is_active === 0} />
              </div>
              <div className="form-group">
                <label className="form-label">Samples Given (DCR)</label>
                <textarea className="form-control" rows={2} value={form.dcr_samples_given} onChange={e => f('dcr_samples_given', e.target.value)} placeholder="Samples distributed details…" disabled={modal.item?.is_active === 0} />
              </div>
              <div className="form-group">
                <label className="form-label">Doctor Feedback</label>
                <textarea className="form-control" rows={2} value={form.dcr_doctor_feedback} onChange={e => f('dcr_doctor_feedback', e.target.value)} placeholder="Feedback from doctor or chemist…" disabled={modal.item?.is_active === 0} />
              </div>
              <div className="form-group">
                <label className="form-label">Next Action</label>
                <input className="form-control" value={form.dcr_next_action} onChange={e => f('dcr_next_action', e.target.value)} placeholder="Follow-up action required…" disabled={modal.item?.is_active === 0} />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: modal.item?.is_active === 0 ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={!!form.dcr_submitted}
                    onChange={e => f('dcr_submitted', e.target.checked)}
                    disabled={modal.item?.is_active === 0}
                  />
                  Submit DCR (mark as officially submitted)
                </label>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* View Modal */}
      <Modal open={viewModal.open} onClose={() => setViewModal({ open: false })} title="Visit Details"
        footer={<button className="btn btn-secondary" onClick={() => setViewModal({ open: false })}>Close</button>}
      >
        {viewModal.visit && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{viewModal.visit.customer_name}</div>
                <div style={{ fontSize: '0.83rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{viewModal.visit.customer_type}{viewModal.visit.specialty ? ` · ${viewModal.visit.specialty}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                <span className={`badge badge-${outcomeColors[viewModal.visit.outcome]}`} style={{ textTransform: 'capitalize' }}>{viewModal.visit.outcome}</span>
                {viewModal.visit.dcr_submitted ? <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>DCR Submitted</span> : null}
              </div>
            </div>
            <div className="detail-grid" style={{ marginBottom: 14 }}>
              <div className="detail-item"><span className="detail-label">Visit Date</span><span className="detail-value">{new Date(viewModal.visit.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</span></div>
              <div className="detail-item"><span className="detail-label">MR</span><span className="detail-value">{viewModal.visit.user_name}</span></div>
              <div className="detail-item"><span className="detail-label">Purpose</span><span className="detail-value">{viewModal.visit.purpose || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Next Visit</span><span className="detail-value">{viewModal.visit.next_visit_date ? new Date(viewModal.visit.next_visit_date).toLocaleDateString('en-IN') : '—'}</span></div>
            </div>
            {viewModal.visit.products_discussed && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Products Discussed</div><div style={{ fontSize: '0.875rem', background: 'var(--primary-lighter)', padding: '8px 12px', borderRadius: 8, color: 'var(--primary-dark)' }}>{viewModal.visit.products_discussed}</div></div>}
            {viewModal.visit.samples_given && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Samples Given</div><div style={{ fontSize: '0.875rem' }}>{viewModal.visit.samples_given}</div></div>}
            {viewModal.visit.notes && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Notes</div><div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)' }}>{viewModal.visit.notes}</div></div>}
            {(viewModal.visit.dcr_purpose || viewModal.visit.dcr_products_discussed || viewModal.visit.dcr_doctor_feedback) && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 6 }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--primary)', marginBottom: 8 }}>DCR Details</div>
                {viewModal.visit.dcr_purpose && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>DCR Purpose</div><div style={{ fontSize: '0.875rem' }}>{viewModal.visit.dcr_purpose}</div></div>}
                {viewModal.visit.dcr_products_discussed && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Products Discussed</div><div style={{ fontSize: '0.875rem' }}>{viewModal.visit.dcr_products_discussed}</div></div>}
                {viewModal.visit.dcr_samples_given && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Samples Given</div><div style={{ fontSize: '0.875rem' }}>{viewModal.visit.dcr_samples_given}</div></div>}
                {viewModal.visit.dcr_doctor_feedback && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Doctor Feedback</div><div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 8 }}>{viewModal.visit.dcr_doctor_feedback}</div></div>}
                {viewModal.visit.dcr_next_action && <div><div className="detail-label" style={{ marginBottom: 2 }}>Next Action</div><div style={{ fontSize: '0.875rem' }}>{viewModal.visit.dcr_next_action}</div></div>}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Freeze Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Visit"
        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}
      >
        <p>Are you sure you want to freeze this visit record for <strong>{deleteConfirm?.customer_name}</strong>? The record will be locked. You can unfreeze it later.</p>
      </Modal>
    </div>
  );
}
